const PUBLIC_FRONT_DOOR_HOSTS = new Set(['run.ceo', 'public.run.ceo']);
const PUBLIC_FRONT_DOOR_PATHS = new Set(['/', '/index.html']);
const PUBLIC_WEBCHAT_PATHS = new Set(['/webchat', '/webchat/', '/webchat/index.html']);
const WORK_BRIDGE_CALLBACK_HOST = 'i.run.ceo';
const TT_CACHE_PREFIX = 'tt-';
const PUBLIC_FRONT_DOOR_REDIRECT = '/saas/';

type LocationLike = {
  hostname: string;
  pathname: string;
  search?: string;
};

type RegistrationLike = {
  unregister: () => Promise<boolean> | boolean;
};

type ServiceWorkerContainerLike = {
  getRegistrations: () => Promise<RegistrationLike[]>;
};

type CacheStorageLike = {
  keys: () => Promise<string[]>;
  delete: (key: string) => Promise<boolean> | boolean;
};

type RetirementParams = {
  location?: LocationLike;
  serviceWorker?: ServiceWorkerContainerLike;
  cacheStorage?: CacheStorageLike;
  redirect?: (href: string) => void;
};

let retirementTriggered = false;

function getGlobalCacheStorage(): CacheStorageLike | undefined {
  if (typeof globalThis === 'undefined' || !('caches' in globalThis)) {
    return undefined;
  }

  return (globalThis as typeof globalThis & { caches?: CacheStorageLike }).caches;
}

export function shouldRetirePublicRootServiceWorker(location?: LocationLike): boolean {
  if (!location) return false;
  const hostname = location.hostname.trim().toLowerCase();
  const pathname = location.pathname || '/';

  return PUBLIC_FRONT_DOOR_HOSTS.has(hostname) && PUBLIC_FRONT_DOOR_PATHS.has(pathname);
}

export function shouldRetirePublicWebchatServiceWorker(location?: LocationLike): boolean {
  if (!location) return false;
  const hostname = location.hostname.trim().toLowerCase();
  const pathname = location.pathname || '/';
  const searchParams = new URLSearchParams(location.search || '');

  if (
    hostname === WORK_BRIDGE_CALLBACK_HOST
    && PUBLIC_WEBCHAT_PATHS.has(pathname)
    && searchParams.get('run_source') === 'work_google_bridge'
  ) {
    return true;
  }

  return PUBLIC_FRONT_DOOR_HOSTS.has(hostname) && PUBLIC_WEBCHAT_PATHS.has(pathname);
}

export function shouldSuppressServiceWorkerRegistration(location?: LocationLike): boolean {
  if (shouldRetirePublicRootServiceWorker(location)) {
    return true;
  }

  if (!location) {
    return false;
  }

  const hostname = location.hostname.trim().toLowerCase();
  const pathname = location.pathname || '/';
  const searchParams = new URLSearchParams(location.search || '');

  return (
    hostname === WORK_BRIDGE_CALLBACK_HOST
    && PUBLIC_WEBCHAT_PATHS.has(pathname)
    && searchParams.get('run_source') === 'work_google_bridge'
  );
}

async function clearRunCaches(cacheStorage?: CacheStorageLike) {
  if (!cacheStorage) return;

  const keys = await cacheStorage.keys();
  await Promise.all(
    keys
      .filter((key) => key.startsWith(TT_CACHE_PREFIX))
      .map((key) => Promise.resolve(cacheStorage.delete(key))),
  );
}

async function unregisterAllServiceWorkers(serviceWorker?: ServiceWorkerContainerLike) {
  if (!serviceWorker) return;

  const registrations = await serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => Promise.resolve(registration.unregister())));
}

export async function retirePublicRootServiceWorker(params: RetirementParams = {}): Promise<boolean> {
  const location = params.location ?? window.location;
  if (!shouldRetirePublicRootServiceWorker(location)) return false;

  await unregisterAllServiceWorkers(params.serviceWorker ?? navigator.serviceWorker);
  await clearRunCaches(params.cacheStorage ?? getGlobalCacheStorage());
  (params.redirect ?? ((href: string) => window.location.replace(href)))(PUBLIC_FRONT_DOOR_REDIRECT);

  return true;
}

export async function retirePublicWebchatServiceWorker(params: RetirementParams = {}): Promise<boolean> {
  const location = params.location ?? window.location;
  if (!shouldRetirePublicWebchatServiceWorker(location)) return false;

  await unregisterAllServiceWorkers(params.serviceWorker ?? navigator.serviceWorker);
  await clearRunCaches(params.cacheStorage ?? getGlobalCacheStorage());

  return true;
}

export function maybeRetirePublicRootServiceWorkerOnBoot(params: RetirementParams = {}): boolean {
  const location = params.location ?? window.location;
  const serviceWorker = params.serviceWorker ?? navigator.serviceWorker;
  const cacheStorage = params.cacheStorage ?? getGlobalCacheStorage();
  const redirect = params.redirect ?? ((href: string) => window.location.replace(href));

  if (retirementTriggered) {
    return false;
  }

  const shouldRetireRoot = shouldRetirePublicRootServiceWorker(location);
  const shouldRetireWebchat = shouldRetirePublicWebchatServiceWorker(location);

  if (!shouldRetireRoot && !shouldRetireWebchat) {
    return false;
  }

  retirementTriggered = true;
  if (shouldRetireRoot) {
    void retirePublicRootServiceWorker({
      location,
      serviceWorker,
      cacheStorage,
      redirect,
    }).catch(() => {
      redirect(PUBLIC_FRONT_DOOR_REDIRECT);
    });
    return true;
  }

  void retirePublicWebchatServiceWorker({
    location,
    serviceWorker,
    cacheStorage,
  }).catch(() => undefined);

  return false;
}

export function resetPublicRootServiceWorkerRetirementForTests() {
  retirementTriggered = false;
}
